import { MediumType } from '@ianesthesie-kit/services';
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import dynamic, { DynamicOptionsLoadingProps } from 'next/dynamic';
import {
  BottomToolbar,
  ImageContainer,
  TopToolbar,
  ViewerContainer
} from './containers';
import {
  Close,
  Delete,
  Download,
  Next,
  OpenInNew,
  Prev,
  Print,
  Reset,
  Rotate,
  ZoomIn,
  ZoomOut
} from './controls';
import { useImageViewer } from './useImageViewer';
import { CenteredContainer, TranslatedText } from '@ianesthesie-kit/core';
import { InfoCircleIcon } from '@/components/icon';
import { FallbackLoader } from '@/components/fallbackLoader';
import { AnimatePresence, motion } from 'framer-motion';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { MedicalDocumentService } from '@/modules/surgicalEvent';

const PdfLoader = (loadingProps: DynamicOptionsLoadingProps) => (
  <FallbackLoader
    fallbackProps={loadingProps}
    loaderProps={{ sx: { color: 'rgb(184, 199, 224)' } }}
  />
);

const PdfDocument = dynamic(
  () => import('./PdfDocument').then((m) => m.PdfDocument),
  { ssr: false, loading: PdfLoader }
);

type Point = {
  x: number;
  y: number;
};

type Rotation = 0 | 90 | 180 | 270;

// TODO integrate in image viewer redux slice
const useMouseDiffOnDrag = (rotation: Rotation, scale = 1) => {
  const [diff, setDiff] = useState<Point>({ x: 0, y: 0 });

  const reset = useCallback(() => {
    setDiff({ x: 0, y: 0 });
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!e.buttons) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      let dx = e.movementX / scale;
      let dy = e.movementY / scale;
      if (rotation === 90) {
        // swap dx and dy
        dx = dx + dy;
        dy = dx - dy;
        dx = dx - dy;
        dy *= -1;
      } else if (rotation === 180) {
        dx *= -1;
        dy *= -1;
      } else if (rotation === 270) {
        dx = dx + dy;
        dy = dx - dy;
        dx = dx - dy;
        dx *= -1;
      }
      setDiff(({ x, y }) => ({ x: x + dx, y: y + dy }));
    },
    [rotation, scale]
  );

  return {
    diff,
    onMouseMove,
    reset
  };
};

export const ImageViewer = () => {
  const {
    currentImage,
    open,
    controls: { zoom, rotation },
    resetZoom,
    resetRotation,
    closeViewer
  } = useImageViewer();
  const toolbarVisibleTimeout =
    useRef<ReturnType<typeof setTimeout>>(undefined);
  const [toolbarsVisible, setToolbarsVisible] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  // Passwords entered for protected PDFs, kept only while the viewer is open
  // (keyed by document id) so navigating away and back doesn't re-prompt.
  const [passwordCache, setPasswordCache] = useState<Record<string, string>>(
    {}
  );
  const invert: Rotation = useMemo(() => {
    if (rotation % 360 === 0) {
      return 0;
    }
    if (rotation % 270 === 0) {
      return 270;
    }

    if (rotation % 180 === 0) {
      return 180;
    }

    if (rotation % 90 === 0) {
      return 90;
    }

    return 0;
  }, [rotation]);

  const {
    diff,
    onMouseMove,
    reset: resetTranslation
  } = useMouseDiffOnDrag(invert, zoom);

  useEffect(() => {
    if (currentImage?.id) {
      setIsResetting(true);
      resetTranslation();
      resetZoom();
      resetRotation();
    }
  }, [currentImage?.id, resetRotation, resetTranslation, resetZoom]);

  useEffect(() => {
    if (!open) {
      resetZoom();
      // Forget cached PDF passwords once the viewer closes.
      setPasswordCache({});
    }
  }, [resetZoom, open]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (isResetting) {
      timeout = setTimeout(() => {
        setIsResetting(false);
      }, 500);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isResetting]);

  const reset = () => {
    setIsResetting(true);
    resetTranslation();
  };

  const showToolbars = () => {
    if (toolbarVisibleTimeout.current) {
      clearTimeout(toolbarVisibleTimeout.current);
    }
    toolbarVisibleTimeout.current = setTimeout(() => {
      setToolbarsVisible(false);
    }, 2000);
    if (toolbarsVisible) {
      return;
    }
    setToolbarsVisible(true);
  };

  if (!currentImage) {
    return null;
  }

  const isImage = currentImage.type === MediumType.IMAGE;
  const isPdf = currentImage.type === MediumType.PDF;
  const isTransformable = isImage || isPdf;
  const innerStyle: React.CSSProperties = {
    transition: isResetting ? 'transform 0.3s 0.1s' : undefined,
    transform: `translate(${diff.x}px, ${diff.y}px)`
  };
  const stopPropagation = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Modal open={open}>
      <ViewerContainer onMouseMove={showToolbars} onClick={() => closeViewer()}>
        <TopToolbar visible={toolbarsVisible}>
          <Typography fontWeight={500} fontSize="large">
            {currentImage.name}
          </Typography>
          <Stack direction="row" gap={2}>
            <Stack direction="row">
              <ZoomOut disabled={!isTransformable} />
              <ZoomIn disabled={!isTransformable} />
              <Reset onReset={reset} disabled={!isTransformable} />
              <Rotate type="right" disabled={!isTransformable} />
            </Stack>
            {isPdf && <Print />}
            {isTransformable && <OpenInNew />}
            <Download />
            {MedicalDocumentService.isDeletable(
              currentImage?.medicalDocument
            ) && <Delete />}
            <Close />
          </Stack>
        </TopToolbar>
        <BottomToolbar visible={toolbarsVisible}>
          <Prev />
          <Next />
        </BottomToolbar>
        {isTransformable && (
          <AnimatePresence mode="popLayout">
            {currentImage.path && (
              <motion.div
                key={currentImage.id}
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0.5 }}
              >
                <ImageContainer
                  draggable="false"
                  onMouseMove={isImage ? onMouseMove : undefined}
                  rotate={rotation}
                  zoom={zoom}
                >
                  {isImage && (
                    <img
                      crossOrigin="anonymous"
                      src={currentImage.path}
                      onClick={stopPropagation}
                      style={innerStyle}
                    />
                  )}
                  {isPdf && (
                    <PdfDocument
                      src={currentImage.path}
                      cachedPassword={passwordCache[currentImage.id]}
                      onPasswordResolved={(pw) =>
                        setPasswordCache((prev) => ({
                          ...prev,
                          [currentImage.id]: pw
                        }))
                      }
                    />
                  )}
                </ImageContainer>
              </motion.div>
            )}
          </AnimatePresence>
        )}
        {!isTransformable && (
          <CenteredContainer flexDirection="column" gap={3}>
            <Box
              sx={{
                typography: 'h2',
                color: 'silver',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '& svg': {
                  width: 48,
                  height: 48
                }
              }}
            >
              <InfoCircleIcon />
              <TranslatedText id="doctor-dashboard.imageViewer.unsupportedFile" />
            </Box>
            <Button
              component={motion.button}
              whileHover={{ y: -4, scale: 1.05 }}
              startIcon={<OpenInNewRoundedIcon />}
              onClick={() => {
                window.open(currentImage.path, '_blank');
              }}
            >
              <TranslatedText id="doctor-dashboard.imageViewer.openInNew" />
            </Button>
          </CenteredContainer>
        )}
      </ViewerContainer>
    </Modal>
  );
};
